// A Discord server's icon (or its initials, as Discord shows a server with none), and a dropdown
// of servers for hosting a game. Servers where you can't storytell are listed but greyed out.

import * as SelectPrimitive from "@radix-ui/react-select";
import { Check, ChevronDown } from "lucide-react";
import { cn } from "../../lib/ui.js";
import type { Guild } from "../../state/auth.js";
import { Tooltip } from "./primitives.js";

type GuildLike = { name: string; iconUrl: string | null };

function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 3)
    .map((word) => word[0])
    .join("");
}

export function GuildIcon({ guild, className }: { guild: GuildLike; className?: string }) {
  if (guild.iconUrl) {
    return <img src={guild.iconUrl} alt="" className={cn("size-6 shrink-0 rounded-full", className)} />;
  }
  return (
    <span
      aria-hidden="true"
      className={cn(
        "grid size-6 shrink-0 place-items-center rounded-full bg-ink-line text-[10px] font-semibold text-parchment",
        className,
      )}
    >
      {initials(guild.name)}
    </span>
  );
}

export function GuildSelect({
  id,
  guilds,
  value,
  onChange,
}: {
  id?: string;
  guilds: Guild[];
  value: string;
  onChange: (guildId: string) => void;
}) {
  return (
    <SelectPrimitive.Root value={value} onValueChange={onChange}>
      <SelectPrimitive.Trigger
        id={id}
        className="flex w-full items-center gap-2 rounded-md border border-ink-line bg-ink px-3 py-1.5 text-left text-sm text-parchment focus:border-brass/60 focus:outline-none"
      >
        <SelectPrimitive.Value placeholder="Pick a server" />
        <SelectPrimitive.Icon className="ml-auto text-parchment-dim">
          <ChevronDown className="size-4" />
        </SelectPrimitive.Icon>
      </SelectPrimitive.Trigger>
      <SelectPrimitive.Portal>
        <SelectPrimitive.Content
          position="popper"
          sideOffset={4}
          className="z-50 w-[var(--radix-select-trigger-width)] overflow-hidden rounded-md border border-ink-line bg-ink shadow-xl"
        >
          <SelectPrimitive.Viewport className="p-1">
            {guilds.map((guild) => (
              <Tooltip key={guild.id} content={guild.canStoryteller ? null : "You're not a storyteller"} side="right">
                <SelectPrimitive.Item
                  value={guild.id}
                  disabled={!guild.canStoryteller}
                  className={cn(
                    "relative flex cursor-pointer select-none items-center rounded px-2 py-1.5 pr-7 text-sm text-parchment outline-none",
                    "data-[highlighted]:bg-brass/20",
                    "data-[disabled]:cursor-not-allowed data-[disabled]:opacity-45",
                  )}
                >
                  <SelectPrimitive.ItemText>
                    <span className="flex items-center gap-2">
                      <GuildIcon guild={guild} className="size-5" />
                      {guild.name}
                    </span>
                  </SelectPrimitive.ItemText>
                  <SelectPrimitive.ItemIndicator className="absolute right-2">
                    <Check className="size-3.5 text-brass" />
                  </SelectPrimitive.ItemIndicator>
                </SelectPrimitive.Item>
              </Tooltip>
            ))}
          </SelectPrimitive.Viewport>
        </SelectPrimitive.Content>
      </SelectPrimitive.Portal>
    </SelectPrimitive.Root>
  );
}
